import React, { useState, useRef, useEffect } from 'react';
import AttachmentIcon from '../icons/AttachmentIcon';
import AddSquareIcon from '../icons/AddSquareIcon';
import SendIcon from '../icons/SendIcon';
import RightArrowIcon from '../icons/RightArrowIcon';
import TrashIcon from '../icons/TrashIcon';
import FileIcon from '../icons/FileIcon';
import FileUploadIcon from '../icons/FileUploadIcon';
import JsonIcon from '../icons/JsonIcon';
import CsvIcon from '../icons/CsvIcon';
import ExcelIcon from '../icons/ExcelIcon';
import PdfIcon from '../icons/PdfIcon';
import PptIcon from '../icons/PptIcon';
import DocxIcon from '../icons/DocxIcon';
import ModeSelector from './ModeSelector';
import './SendMessageInput.css';

interface SendMessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  attachedFiles: File[];
  onFileSelect: () => void;
  onFileRemove: (index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  isDragOver: boolean;
  variant?: 'default' | 'chat';
  disabled?: boolean;
  isAgentMode?: boolean;
  onAgentModeChange?: (value: boolean) => void;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'json':
      return JsonIcon;
    case 'csv':
      return CsvIcon;
    case 'xls':
    case 'xlsx':
      return ExcelIcon;
    case 'pdf':
      return PdfIcon;
    case 'ppt':
    case 'pptx':
      return PptIcon;
    case 'doc':
    case 'docx':
      return DocxIcon;
    default:
      return FileIcon;
  }
};

const SendMessageInput: React.FC<SendMessageInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  attachedFiles,
  onFileSelect,
  onFileRemove,
  onDragOver,
  onDragLeave,
  onDrop,
  onPaste,
  isDragOver,
  variant = 'default',
  disabled = false,
  isAgentMode = true,
  onAgentModeChange
}) => {
  // Set placeholder based on variant and mode
  let defaultPlaceholder = 'Ask Anything...';
  if (variant === 'chat') {
    defaultPlaceholder = 'Ask Something';
  } else if (variant === 'default') {
    // For default variant, change based on agent mode
    defaultPlaceholder = isAgentMode
      ? 'Describe the project for your dashboard...'
      : 'Ask me anything';
  }
  const effectivePlaceholder = placeholder || defaultPlaceholder;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-resize textarea as content changes
  useEffect(() => {
    const textarea = variant === 'chat' ? chatTextareaRef.current : textareaRef.current;
    if (textarea) {
      // Reset height to recalculate
      textarea.style.height = 'auto';
      // Set height based on scrollHeight
      const newHeight = Math.min(textarea.scrollHeight, 120); // Max height 120px
      textarea.style.height = `${newHeight}px`;
    }
  }, [value, variant]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = () => {
    // Prevent submission when disabled
    if (disabled) return;
    // Prevent multiple rapid submissions
    if (isSubmitting || !value.trim()) return;

    setIsSubmitting(true);
    onSubmit();

    // Re-enable after 1 second to prevent accidental double-clicks
    submitTimeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
    }, 1000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Send message on Enter, but allow new line with Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default new line
      // Only submit if not disabled
      if (!disabled) {
        handleSubmit();
      }
    }
  };

  const containerClass = variant === 'chat'
    ? `message-input-container chat-input-container ${isDragOver ? 'drag-over' : ''}`
    : `message-input-container question-input-container ${isDragOver ? 'drag-over' : ''}`;

  const inputClass = variant === 'chat' ? 'message-input-base chat-input' : 'message-input-base question-input';
  const buttonClass = variant === 'chat' ? 'primary-btn-base send-btn' : 'primary-btn-base submit-btn';
  const rowClass = 'input-row';

  if (variant === 'default') {
    return (
      <div
        className={containerClass}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={onPaste}
        tabIndex={0}
      >
        {isDragOver && (
          <div className="drag-overlay">
            <FileUploadIcon size={72} />
            <p className="drag-overlay-text">Drop your file here</p>
            <p className="drag-overlay-text-allowed">Allowed formats: PDF, PPT, PPTX, DOC, DOCX, CSV, XLS, XLSX, JSON.</p>
          </div>
        )}
        <div className="input-area">
          <div className="textarea-row">
            <div className="input-wrapper-no-attach">
              <textarea
                ref={textareaRef}
                className={inputClass}
                placeholder={effectivePlaceholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={false} // Always allow typing
                rows={4}
                style={{
                  minHeight: '80px',
                  maxHeight: '120px',
                  resize: 'none',
                  overflow: 'auto'
                }}
              />
            </div>
          </div>

          <div className="actions-row">
            <div className="actions-left">
              <button
                className="upload-file-btn"
                onClick={onFileSelect}
                aria-label="Upload file"
                disabled={disabled}
              >
                <span className="upload-file-icon">+</span>
                <span className="upload-file-text">Upload File</span>
              </button>

              {/* Mode Selector Dropdown */}
              {onAgentModeChange && (
                <ModeSelector
                  value={isAgentMode}
                  onChange={(newMode) => {
                    console.log('[SendMessageInput] ModeSelector onChange called with:', newMode);
                    onAgentModeChange(newMode);
                  }}
                />
              )}
            </div>

            <button
              className={buttonClass}
              onClick={handleSubmit}
              disabled={disabled || !value.trim() || isSubmitting}
              aria-label="Submit question"
            >
              <RightArrowIcon size={18} />
            </button>
          </div>

          {attachedFiles.length > 0 && (
            <>
              <div className="attachments-separator"></div>
              <div className="attached-files-inline">
                <div className="files-grid-inline">
                  {attachedFiles.map((file, index) => {
                    const IconComponent = getFileIcon(file.name);
                    const formatFileSize = (bytes: number) => {
                      if (bytes < 1024) return bytes + ' B';
                      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                    };
                    return (
                      <div key={index} className="file-card">
                        <div className="file-card-content">
                          <IconComponent className="file-icon" size={32} />
                          <div className="file-info">
                            <span className="file-name" title={file.name}>
                              {file.name}
                            </span>
                            <span className="file-size">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                        </div>
                        <button
                          className="remove-file-btn"
                          onClick={() => onFileRemove(index)}
                          aria-label="Remove file"
                        >
                          <TrashIcon size={18} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <small className="ai-disclaimer">This feature is powered by AI. Outputs may contain inaccuracies. Please verify critical information before use.</small>
      </div>
    );
  }

  // Chat variant
  return (
    <div
      className={containerClass}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onPaste={onPaste}
      tabIndex={0}
    >
      {isDragOver && (
        <div className="drag-overlay">
          <FileUploadIcon size={72} />
          <p className="drag-overlay-text">Drop your file here</p>
        </div>
      )}
      <div className={rowClass}>
        <button
          className="chat-attach-btn"
          onClick={onFileSelect}
          aria-label="Attach file"
          disabled={disabled}
        >
          <AddSquareIcon size={24} />
        </button>

        <textarea
          ref={chatTextareaRef}
          className={inputClass}
          placeholder={effectivePlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={false} // Always allow typing
          rows={1}
          style={{
            minHeight: '40px',
            maxHeight: '120px',
            resize: 'none',
            overflow: 'auto'
          }}
        />

        <button
          className="chat-send-btn"
          onClick={handleSubmit}
          disabled={disabled || !value.trim() || isSubmitting}
          aria-label="Send message"
        >
          <RightArrowIcon size={16} />
        </button>
      </div>

      {attachedFiles.length > 0 && (
        <div className="attached-files">
          <div className="files-list">
            {attachedFiles.map((file, index) => {
              const IconComponent = getFileIcon(file.name);
              return (
                <div key={index} className="file-card">
                  <div className="file-card-content">
                    <IconComponent className="file-icon" size={16} />
                    <span className="file-name" title={file.name}>
                      {file.name}
                    </span>
                  </div>
                  <button
                    className="remove-file-btn"
                    onClick={() => onFileRemove(index)}
                    aria-label="Remove file"
                  >
                    <TrashIcon size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SendMessageInput;